import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  limit,
  startAfter,
  startAt,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage, logAnalyticsEvent } from './firebase'
import { ExtendedUser } from '../context/AuthContext'

export interface Article {
  id?: string
  title: string
  slug: string
  content: string
  excerpt: string
  featuredImage?: string
  author: {
    id: string
    name: string
    photoURL?: string
  }
  status: 'draft' | 'published' | 'archived'
  tags: string[]
  categoryId?: string
  categoryName?: string
  createdAt: Timestamp
  updatedAt: Timestamp
  publishedAt?: Timestamp
  scheduledFor?: Timestamp // New field for scheduled publishing
  viewCount?: number
  featured?: boolean
  readTime?: number // Estimated read time in minutes
}

export interface Comment {
  id?: string
  articleId: string
  content: string
  author: {
    id: string
    name: string
    photoURL?: string
  }
  status: 'pending' | 'approved' | 'rejected'
  createdAt: Timestamp
}

// Generate a slug from title
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w\s]/gi, '')
    .replace(/\s+/g, '-')
}

// Get all published articles
export const getPublishedArticles = async (
  limitCount?: number,
  startAfterDoc?: any,
) => {
  try {
    console.log('Fetching published articles...')
    const articlesRef = collection(db, 'articles')
    // Create a simpler query that will work without complex indices
    // First, just filter by status
    let q = query(
      articlesRef,
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
    )
    if (startAfterDoc) {
      q = query(q, startAfter(startAfterDoc))
    }
    if (limitCount) {
      q = query(q, limit(limitCount))
    }
    console.log('Executing Firestore query...')
    const snapshot = await getDocs(q)
    console.log(`Found ${snapshot.docs.length} articles`)
    // Filter scheduled articles client-side
    const now = Timestamp.now()
    const articles = snapshot.docs
      .map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Article,
      )
      .filter((article) => {
        // Only include articles that aren't scheduled for the future
        if (article.scheduledFor) {
          return article.scheduledFor <= now
        }
        return true
      })
    return {
      articles,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
    }
  } catch (error) {
    console.error('Error fetching published articles:', error)
    throw error
  }
}

// Get featured articles
export const getFeaturedArticles = async (limitCount = 3) => {
  try {
    console.log('Fetching featured articles...')
    const articlesRef = collection(db, 'articles')
    // Simplified query structure
    const q = query(
      articlesRef,
      where('status', '==', 'published'),
      where('featured', '==', true),
      orderBy('publishedAt', 'desc'),
      limit(limitCount),
    )
    const snapshot = await getDocs(q)
    console.log(`Found ${snapshot.docs.length} featured articles`)
    // Filter scheduled articles client-side
    const now = Timestamp.now()
    return snapshot.docs
      .map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Article,
      )
      .filter((article) => {
        if (article.scheduledFor) {
          return article.scheduledFor <= now
        }
        return true
      })
  } catch (error) {
    console.error('Error fetching featured articles:', error)
    throw error
  }
}

// Get scheduled articles
export const getScheduledArticles = async () => {
  try {
    console.log('Fetching scheduled articles...')
    const articlesRef = collection(db, 'articles')
    const now = Timestamp.now()
    // First get all published articles
    const q = query(
      articlesRef,
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
    )
    const snapshot = await getDocs(q)
    console.log(
      `Found ${snapshot.docs.length} published articles, filtering for scheduled`,
    )
    // Then filter for scheduled ones client-side
    return snapshot.docs
      .map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as Article,
      )
      .filter((article) => article.scheduledFor && article.scheduledFor > now)
      .sort((a, b) => {
        // Sort by scheduledFor date (ascending)
        if (a.scheduledFor && b.scheduledFor) {
          return a.scheduledFor.seconds - b.scheduledFor.seconds
        }
        return 0
      })
  } catch (error) {
    console.error('Error fetching scheduled articles:', error)
    throw error
  }
}

// Get article by ID
export const getArticleById = async (id: string) => {
  try {
    console.log(`Fetching article with ID: ${id}`)
    const docRef = doc(db, 'articles', id)
    const docSnap = await getDoc(docRef)
    if (docSnap.exists()) {
      console.log('Article found')
      return { id: docSnap.id, ...docSnap.data() } as Article
    }
    console.log('Article not found')
    return null
  } catch (error) {
    console.error(`Error fetching article with ID ${id}:`, error)
    throw error
  }
}

// Get article by slug
export const getArticleBySlug = async (slug: string) => {
  try {
    console.log(`Fetching article with slug: ${slug}`)
    const articlesRef = collection(db, 'articles')
    const q = query(articlesRef, where('slug', '==', slug))
    const snapshot = await getDocs(q)
    if (!snapshot.empty) {
      console.log('Article found by slug')
      const doc = snapshot.docs[0]
      return { id: doc.id, ...doc.data() } as Article
    }
    console.log('Article not found by slug')
    return null
  } catch (error) {
    console.error(`Error fetching article with slug ${slug}:`, error)
    throw error
  }
}

// Get related articles based on category and tags
export const getRelatedArticles = async (article: Article, limit = 3) => {
  if (!article.id) return []
  try {
    console.log(`Finding related articles for article ID: ${article.id}`)
    const articlesRef = collection(db, 'articles')
    let relatedArticles: Article[] = []
    // First try to find articles with the same category
    if (article.categoryId) {
      const categoryQuery = query(
        articlesRef,
        where('status', '==', 'published'),
        where('categoryId', '==', article.categoryId),
        limit(limit),
      )
      const snapshot = await getDocs(categoryQuery)
      console.log(`Found ${snapshot.docs.length} articles in the same category`)
      const categoryArticles = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }) as Article)
        .filter((a) => a.id !== article.id)
      relatedArticles = [...categoryArticles]
    }
    // If we don't have enough articles, get the most recent ones
    if (relatedArticles.length < limit) {
      const recentQuery = query(
        articlesRef,
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc'),
        limit(limit + 5), // Get extra to account for filtering
      )
      const snapshot = await getDocs(recentQuery)
      console.log(`Found ${snapshot.docs.length} recent articles`)
      const recentArticles = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }) as Article)
        .filter(
          (a) =>
            a.id !== article.id && !relatedArticles.some((r) => r.id === a.id),
        )
        .slice(0, limit - relatedArticles.length)
      relatedArticles = [...relatedArticles, ...recentArticles]
    }
    return relatedArticles
  } catch (error) {
    console.error('Error fetching related articles:', error)
    return []
  }
}

// Create a new article
export const createArticle = async (
  articleData: Partial<Article>,
  user: ExtendedUser,
) => {
  const slug = generateSlug(articleData.title || 'untitled')
  // Calculate estimated read time
  const readTime = articleData.content
    ? calculateReadTime(articleData.content)
    : 3
  const newArticle = {
    title: articleData.title || 'Untitled',
    slug,
    content: articleData.content || '',
    excerpt: articleData.excerpt || '',
    featuredImage: articleData.featuredImage || '',
    author: {
      id: user.uid,
      name: user.displayName || 'Unknown',
      photoURL: user.photoURL || '',
    },
    status: articleData.status || 'draft',
    tags: articleData.tags || [],
    categoryId: articleData.categoryId || '',
    categoryName: articleData.categoryName || '',
    featured: articleData.featured || false,
    viewCount: 0,
    readTime,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    scheduledFor: articleData.scheduledFor || null,
  }
  const docRef = await addDoc(collection(db, 'articles'), newArticle)
  return { id: docRef.id, ...newArticle }
}

// Update an article
export const updateArticle = async (
  id: string,
  articleData: Partial<Article>,
) => {
  const articleRef = doc(db, 'articles', id)
  // If title is changed, update slug
  if (articleData.title) {
    articleData.slug = generateSlug(articleData.title)
  }
  // If content is updated, recalculate read time
  if (articleData.content) {
    articleData.readTime = calculateReadTime(articleData.content)
  }
  // Add updated timestamp
  const updateData = {
    ...articleData,
    updatedAt: serverTimestamp(),
  }
  // If status is changing to published and no scheduled date, add published timestamp
  if (
    articleData.status === 'published' &&
    !articleData.publishedAt &&
    !articleData.scheduledFor
  ) {
    updateData.publishedAt = serverTimestamp()
  }
  await updateDoc(articleRef, updateData)
  return { id, ...articleData }
}

// Calculate estimated read time in minutes
export const calculateReadTime = (content: string): number => {
  // Remove HTML tags
  const text = content.replace(/<[^>]*>?/gm, '')
  // Average reading speed: 200 words per minute
  const words = text.trim().split(/\s+/).length
  const readTimeMinutes = Math.ceil(words / 200)
  return Math.max(1, readTimeMinutes) // Minimum 1 minute
}

// Increment view count
export const incrementViewCount = async (id: string) => {
  const articleRef = doc(db, 'articles', id)
  const article = await getArticleById(id)
  if (article) {
    const currentCount = article.viewCount || 0
    await updateDoc(articleRef, {
      viewCount: currentCount + 1,
    })
    // Log view event for analytics
    logAnalyticsEvent('article_view', {
      article_id: id,
      article_title: article.title,
      category_id: article.categoryId || 'none',
      category_name: article.categoryName || 'none',
    })
    return currentCount + 1
  }
  return 0
}

// Track article engagement
export const trackArticleEngagement = (
  id: string,
  engagementType: 'read' | 'share' | 'comment',
  additionalData = {},
) => {
  logAnalyticsEvent('article_engagement', {
    article_id: id,
    engagement_type: engagementType,
    ...additionalData,
  })
}

// Delete an article
export const deleteArticle = async (id: string) => {
  await deleteDoc(doc(db, 'articles', id))
  return id
}

// Upload an image for an article
export const uploadArticleImage = async (file: File, articleId: string) => {
  const storageRef = ref(storage, `articles/${articleId}/${file.name}`)
  await uploadBytes(storageRef, file)
  return getDownloadURL(storageRef)
}

// Advanced search for articles
export const searchArticles = async (params: {
  searchTerm?: string
  categoryId?: string
  tags?: string[]
  startDate?: Date
  endDate?: Date
  featured?: boolean
  limit?: number
  startAfterDoc?: any
}) => {
  const {
    searchTerm,
    categoryId,
    tags,
    startDate,
    endDate,
    featured,
    limit: limitCount = 10,
    startAfterDoc,
  } = params
  const articlesRef = collection(db, 'articles')
  // Start with base query
  let q = query(
    articlesRef,
    where('status', '==', 'published'),
    where('scheduledFor', '<=', serverTimestamp()),
    orderBy('scheduledFor', 'desc'),
    orderBy('publishedAt', 'desc'),
  )
  // Add category filter if specified
  if (categoryId) {
    q = query(q, where('categoryId', '==', categoryId))
  }
  // Add featured filter if specified
  if (featured) {
    q = query(q, where('featured', '==', true))
  }
  // Add pagination
  if (startAfterDoc) {
    q = query(q, startAfter(startAfterDoc))
  }
  // Add limit
  q = query(q, limit(limitCount))
  // Execute query
  const snapshot = await getDocs(q)
  // Get results and filter client-side for remaining criteria
  let articles = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Article[]
  // Filter by tags if specified
  if (tags && tags.length > 0) {
    articles = articles.filter(
      (article) =>
        article.tags && article.tags.some((tag) => tags.includes(tag)),
    )
  }
  // Filter by date range if specified
  if (startDate) {
    const startTimestamp = Timestamp.fromDate(startDate)
    articles = articles.filter(
      (article) => article.publishedAt && article.publishedAt >= startTimestamp,
    )
  }
  if (endDate) {
    const endTimestamp = Timestamp.fromDate(endDate)
    articles = articles.filter(
      (article) => article.publishedAt && article.publishedAt <= endTimestamp,
    )
  }
  // Filter by search term if specified
  if (searchTerm && searchTerm.trim() !== '') {
    const term = searchTerm.toLowerCase().trim()
    articles = articles.filter(
      (article) =>
        article.title.toLowerCase().includes(term) ||
        article.excerpt.toLowerCase().includes(term) ||
        (article.tags &&
          article.tags.some((tag) => tag.toLowerCase().includes(term))),
    )
  }
  return {
    articles,
    lastDoc: snapshot.docs[snapshot.docs.length - 1],
  }
}

// Get all comments for an article
export const getArticleComments = async (
  articleId: string,
  includeUnapproved = false,
) => {
  const commentsRef = collection(db, 'comments')
  let q
  if (includeUnapproved) {
    q = query(
      commentsRef,
      where('articleId', '==', articleId),
      orderBy('createdAt', 'desc'),
    )
  } else {
    q = query(
      commentsRef,
      where('articleId', '==', articleId),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc'),
    )
  }
  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Comment[]
}

// Add a comment to an article
export const addComment = async (
  articleId: string,
  content: string,
  user: ExtendedUser,
) => {
  const newComment = {
    articleId,
    content,
    author: {
      id: user.uid,
      name: user.displayName || 'Anonymous',
      photoURL: user.photoURL || '',
    },
    status: user.role === 'pending' ? 'pending' : 'approved', // Auto-approve for non-pending users
    createdAt: serverTimestamp(),
  }
  const docRef = await addDoc(collection(db, 'comments'), newComment)
  // Track comment engagement
  trackArticleEngagement(articleId, 'comment', {
    comment_id: docRef.id,
    user_id: user.uid,
  })
  return { id: docRef.id, ...newComment }
}

// Update comment status (for moderation)
export const updateCommentStatus = async (
  commentId: string,
  status: 'approved' | 'rejected',
) => {
  const commentRef = doc(db, 'comments', commentId)
  await updateDoc(commentRef, { status })
  return commentId
}

// Get all pending comments (for moderation)
export const getPendingComments = async () => {
  const commentsRef = collection(db, 'comments')
  const q = query(
    commentsRef,
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc'),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Comment[]
}
